import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  FolderKanban,
  Image,
  Settings,
  Mail,
  LogOut,
  Menu,
  ChevronLeft,
  Sun,
  Moon,
  Search,
  User,
  Wrench,
  Star,
  PackageCheck,
} from 'lucide-react';
import { authAPI } from '../../api/auth';
import { useTheme } from '../../context/ThemeContext';
import NotificationDropdown from './NotificationDropdown';
import './Dashboard.css';

const ROUTE_LABELS = {
  '/': 'Dashboard',
  '/projects': 'Projects',
  '/gallery': 'Gallery',
  '/services': 'Services',
  '/packages': 'Packages',
  '/reviews': 'Reviews',
  '/settings': 'Site Settings',
};

const menuItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/projects', label: 'Projects', icon: FolderKanban },
  { path: '/gallery', label: 'Gallery', icon: Image },
  { path: '/services', label: 'Services', icon: Wrench },
  { path: '/packages', label: 'Packages', icon: PackageCheck },
  { path: '/inquiries', label: 'Inquiries', icon: Mail },
  { path: '/reviews', label: 'Reviews', icon: Star },
  { path: '/settings', label: 'Site Settings', icon: Settings },
];

function getBreadcrumbs(pathname) {
  if (pathname === '/') return [{ path: '/', label: 'Dashboard' }];
  const parts = pathname.split('/').filter(Boolean);
  const result = [];
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    const path = '/' + parts.slice(0, i + 1).join('/');
    let label = ROUTE_LABELS[path];
    if (!label) {
      if (p === 'new') label = 'New';
      else if (/^\d+$/.test(p)) {
        if (parts[i - 1] === 'edit' && result.length > 0) {
          result[result.length - 1].path = path;
          continue;
        }
        label = 'Edit';
      } else {
        label = p.charAt(0).toUpperCase() + p.slice(1).replace(/-/g, ' ');
      }
    }
    result.push({ path, label });
  }
  return result;
}

const Dashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((s) => !s);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const breadcrumbs = getBreadcrumbs(location.pathname);

  return (
    <div className={`studio-layout ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
      <aside
        className={`studio-sidebar ${sidebarOpen ? 'open' : 'closed'} ${theme}`}
      >
        <div className="sidebar-header">
          <Link to="/" className="sidebar-brand">
            <img src="/logo-black.png" alt="IKP Studio" className="sidebar-logo-img" />
            {sidebarOpen && <span className="sidebar-title">IKP Studio</span>}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="sidebar-toggle"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <ChevronLeft size={22} /> : <Menu size={24} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path ||
              (item.path !== '/' && location.pathname.startsWith(item.path));
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
              >
                <Icon
                  size={20}
                  className={isActive ? 'icon-glow' : ''}
                  strokeWidth={isActive ? 2.5 : 1.5}
                />
                {sidebarOpen && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <User size={20} className="user-icon" />
            {sidebarOpen && (
              <span className="user-role">
                {(user.role || 'admin').charAt(0).toUpperCase() + (user.role || 'admin').slice(1)}
              </span>
            )}
          </div>
          <button onClick={handleLogout} className="logout-btn">
            <LogOut size={18} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      <div className="studio-main">
        <header className="studio-navbar">
          <div className="navbar-left">
            <div className="breadcrumbs">
              {breadcrumbs.map((b, i) => (
                <span key={b.path + i}>
                  {i > 0 && <span className="breadcrumb-sep">/</span>}
                  {i === breadcrumbs.length - 1 ? (
                    <span className="breadcrumb-current">{b.label}</span>
                  ) : (
                    <Link to={b.path} className="breadcrumb-link">
                      {b.label}
                    </Link>
                  )}
                </span>
              ))}
            </div>
          </div>
          <div className="navbar-right">
            <button
              onClick={() => setSearchOpen(true)}
              className="search-trigger"
              title="Search (Cmd+K)"
            >
              <Search size={18} />
              <span className="search-placeholder">Search...</span>
              <kbd>⌘K</kbd>
            </button>
            <NotificationDropdown />
            <button
              onClick={toggleTheme}
              className="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </header>

        <main className="studio-content">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={`content-inner ${location.pathname === '/inquiries' || location.pathname === '/reviews' ? 'content-inner-wide' : ''}`}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {searchOpen && (
        <div className="search-overlay" onClick={() => setSearchOpen(false)}>
          <div
            className="search-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Global search"
          >
            <div className="search-input-wrap">
              <Search size={20} />
              <input
                type="search"
                placeholder="Search projects, services, gallery..."
                autoFocus
                onKeyDown={(e) => e.key === 'Escape' && setSearchOpen(false)}
              />
            </div>
            <p className="search-hint">Press Esc to close</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
